import { useEffect, useRef, useState } from 'react';
import { Button, ClickAwayListener, Fade, IconButton, Portal, Tooltip } from '@mui/material';
import { useLocation } from 'react-router-dom';
import LeadStreamsService from 'src/services/LeadStreamsService';
import { parseStreamList } from 'src/services/LeadStreamsService/streamList';
import useSessionStore from 'src/stores/session';
import useFilterStore from 'src/stores/filter';
import useUserStore from 'src/stores/user';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import { history } from 'src/utils/history';
import { SUPPORT_URL } from 'src/constants/support';
import Cross from 'src/icons/Cross';
import ArrowLeft from 'src/icons/ArrowLeft';
import LeadsmaniaCollapsedLogo from 'src/icons/LeadsmaniaCollapsedLogo';
import { getStreamDisplayName } from 'src/utils/leadStreams';

import CloudOrb from './CloudOrb';
import { parseStreamCommand } from './streamCommand';
import useStreamCommand from './useStreamCommand';
import StreamCommandCard from './StreamCommandCard';
import { diagnoseDelivery, isDeliveryQuestion, requestedStreamId, streamIdFromPage } from './diagnostics';
import { answerFromCabinet } from './model';
import { allowedCompanionPath as allowedPath } from './actions';

import useCompanionStatus from './useCompanionStatus';
import { askAssistant } from './assistant';

import type { CompanionEvent } from './actions';

import type { ChatMessage } from './assistant';
import './companion.css';

const storageKey = (id: number) => `leadsmania:companion:${id}`;
function readChat(userId: number): ChatMessage[] {
    try {
        const data = JSON.parse(localStorage.getItem(storageKey(userId)) || '[]');
        if (!Array.isArray(data)) return [];
        return data
            .slice(-30)
            .filter((m) => m && ['user', 'assistant'].includes(m.role) && typeof m.text === 'string')
            .map((m, i) => ({
                id: i,
                role: m.role,
                text: m.text.slice(0, 2400),
                ai: !!m.ai,
                actions: Array.isArray(m.actions)
                    ? m.actions
                          .filter(
                              (a: any) =>
                                  typeof a?.label === 'string' && typeof a?.path === 'string' && allowedPath(a.path)
                          )
                          .slice(0, 6)
                    : undefined,
            }));
    } catch {
        return [];
    }
}

export function Companion({ userId }: { userId: number }) {
    const mobile = useIsMobileSize();
    const location = useLocation();
    const { streams, summary, available, events, unreadEvents, acknowledge, reviewUnavailable, refresh, refreshing } =
        useCompanionStatus(userId);
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState<ChatMessage[]>(() => readChat(userId));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [retry, setRetry] = useState('');
    const [unread, setUnread] = useState(false);
    const request = useRef<AbortController | null>(null);
    const diagnosticScope = useRef<string | null>(null);
    const log = useRef<HTMLDivElement>(null);
    const field = useRef<HTMLTextAreaElement>(null);
    const launcher = useRef<HTMLButtonElement>(null);
    const panel = useRef<HTMLElement>(null);
    const followBottom = useRef(true);
    const logScrollTop = useRef(0);
    const openRef = useRef(open);
    openRef.current = open;
    const nextId = useRef(Date.now());
    const nextEvent = unreadEvents[0];
    const [noticeVisible, setNoticeVisible] = useState(false);
    const noticeKey = nextEvent ? `${nextEvent.id}:${nextEvent.version}` : '';
    useEffect(() => {
        setNoticeVisible(!!noticeKey);
        const timer = window.setTimeout(() => setNoticeVisible(false), 10000);
        return () => window.clearTimeout(timer);
    }, [noticeKey]);
    const currentStreamId = streamIdFromPage(location.pathname, location.search);
    const pageScope = `${location.pathname}${location.search}`;
    const currentStream = streams.find((s) => s.id === currentStreamId);
    const command = useStreamCommand(userId, pageScope, (reply) => append({ role: 'assistant', ...reply }));
    const state =
        busy || command.busy
            ? 'thinking'
            : command.state?.phase === 'uncertain'
            ? 'attention'
            : nextEvent
            ? nextEvent.kind === 'delivery'
                ? 'found'
                : 'attention'
            : summary.state;
    const busyLabel = command.busy ? 'Проверяю команду' : diagnosticScope.current !== null ? 'Проверяю поток' : 'Думаю';
    const label =
        busy || command.busy
            ? busyLabel
            : command.state?.phase === 'uncertain'
            ? 'Проверьте результат команды'
            : nextEvent
            ? nextEvent.title
            : summary.label;
    const unreadLabel = error ? 'Не удалось получить ответ' : 'Ответ готов';
    const previousCommand = useRef(!!command.state);
    useEffect(() => {
        if (previousCommand.current && !command.state && open && !mobile) field.current?.focus({ preventScroll: true });
        previousCommand.current = !!command.state;
    }, [command.state, open, mobile]);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);
    useEffect(() => {
        if (diagnosticScope.current !== null && diagnosticScope.current !== pageScope) {
            request.current?.abort();
            request.current = null;
            diagnosticScope.current = null;
            setBusy(false);
            setError('Страница изменилась. Проверку можно запустить для текущего потока.');
            setRetry('');
        }
    }, [pageScope]);
    useEffect(
        () => () => {
            request.current?.abort();
        },
        []
    );
    useEffect(() => {
        try {
            localStorage.setItem(storageKey(userId), JSON.stringify(messages.slice(-30)));
        } catch {
            /* Chat still works without storage. */
        }
    }, [messages, userId]);
    useEffect(() => {
        if (followBottom.current && log.current) log.current.scrollTop = log.current.scrollHeight;
    }, [messages, busy, error, open, command.state]);

    const close = (restoreFocus = false) => {
        setOpen(false);
        if (restoreFocus) launcher.current?.focus({ preventScroll: true });
    };
    const toggle = () => {
        if (open) close(true);
        else {
            setUnread(false);
            setNoticeVisible(false);
            setOpen(true);
        }
    };
    const append = (message: Omit<ChatMessage, 'id'>) => {
        setMessages((items) => [...items, { ...message, id: nextId.current++ }].slice(-30));
        if (message.role === 'assistant' && !openRef.current) setUnread(true);
    };
    const send = async (text: string, repeat = false) => {
        const question = text.trim().slice(0, 600);
        if (!question || request.current || command.state) return;
        followBottom.current = true;
        setInput('');
        setError('');
        setRetry('');
        const intent = parseStreamCommand(question);
        if (intent) {
            append({ role: 'user', text: question });
            void command.begin(intent, currentStreamId);
            return;
        }
        const diagnostic = isDeliveryQuestion(question);
        const scopedStreams = currentStream && /эт(от|им|ом|ого)/i.test(question) ? [currentStream] : streams;
        const matchingEvents = events.filter((e) => !/новые лиды/i.test(question) || e.kind === 'delivery');
        const localReply = diagnostic
            ? null
            : /^(что требует внимания|покажи новые лиды)[?.!]*$/i.test(question)
            ? {
                  text: matchingEvents.length
                      ? 'Можно открыть нужный поток или очередь.'
                      : available && !reviewUnavailable
                      ? 'Новых событий пока нет.'
                      : 'Не удалось обновить все события. Повторите обновление состояния.',
                  actions: matchingEvents.map((e) => ({ label: `${e.subject}: ${e.title}`, path: e.path })),
              }
            : answerFromCabinet(question, scopedStreams, available);
        if (!repeat) append({ role: 'user', text: question, ai: !diagnostic && !localReply });
        if (localReply) {
            append({ role: 'assistant', ...localReply });
            return;
        }
        const controller = new AbortController();
        request.current = controller;
        diagnosticScope.current = diagnostic ? pageScope : null;
        setBusy(true);
        const timeout = window.setTimeout(() => controller.abort(), 30000);
        try {
            if (diagnostic) {
                if (!navigator.onLine) throw new Error('offline');
                const targetId = requestedStreamId(question, currentStreamId);
                const fresh = targetId
                    ? parseStreamList([await LeadStreamsService.getStream(targetId, controller.signal)])
                    : await LeadStreamsService.getStreams({ signal: controller.signal });
                if (controller.signal.aborted) throw new Error('aborted');
                if (targetId && fresh[0]?.id !== targetId) throw new Error('wrong stream');
                if (!navigator.onLine) throw new Error('offline');
                if (request.current === controller && !controller.signal.aborted)
                    append({ role: 'assistant', ...diagnoseDelivery(fresh, targetId) });
                return;
            }
            // Only general consultant turns leave the cabinet; local status replies
            // are omitted to keep stream names and balances out of the public API.
            const conversation = messages.filter((m) => m.ai);
            if (repeat && conversation[conversation.length - 1]?.role === 'user') conversation.pop();
            const reply = await askAssistant(question, conversation, controller.signal);
            if (request.current === controller && !controller.signal.aborted)
                append({ role: 'assistant', text: reply, ai: true });
        } catch (e) {
            if (request.current === controller) {
                setError(
                    diagnostic
                        ? 'Не удалось проверить поток. Повторите проверку — прежние данные не объясняют причину.'
                        : controller.signal.aborted
                        ? 'Ответ не пришёл вовремя. Попробуйте ещё раз.'
                        : e instanceof Error
                        ? e.message
                        : 'Не удалось получить ответ.'
                );
                setRetry(question);
                if (!openRef.current) setUnread(true);
            }
        } finally {
            window.clearTimeout(timeout);
            if (request.current === controller) {
                request.current = null;
                diagnosticScope.current = null;
                setBusy(false);
            }
        }
    };
    const stop = () => {
        request.current?.abort();
        request.current = null;
        setBusy(false);
        setError(diagnosticScope.current !== null ? 'Проверка остановлена.' : 'Ответ остановлен.');
        diagnosticScope.current = null;
        setRetry(messages[messages.length - 1]?.role === 'user' ? messages[messages.length - 1].text : '');
    };
    const go = (path: string) => {
        if (allowedPath(path)) {
            setOpen(false);
            events.filter((event) => event.path === path).forEach(acknowledge);
            if (path.startsWith('/feed?stream=')) useFilterStore.getState().clearFilter();
            history.push(path);
        }
    };

    const openEvent = (event: CompanionEvent) => {
        setNoticeVisible(false);
        go(event.path);
    };

    return (
        <Portal>
            <ClickAwayListener onClickAway={() => close()}>
                <div
                    className="lm-companion-dock"
                    data-state={state}
                    onKeyDown={(e) => {
                        if (open && e.key === 'Escape') {
                            e.preventDefault();
                            e.stopPropagation();
                            close(true);
                        }
                    }}
                >
                    {nextEvent && noticeVisible && !open && (
                        <div className="lm-companion-notice">
                            <button
                                className="lm-companion-notice-action"
                                onClick={() => openEvent(nextEvent)}
                            >
                                <strong>{nextEvent.title}</strong>
                                <span>{nextEvent.subject} ↗</span>
                            </button>
                            <button
                                className="lm-companion-notice-dismiss"
                                aria-label="Скрыть уведомление"
                                onClick={() => {
                                    acknowledge(nextEvent);
                                    setNoticeVisible(false);
                                }}
                            >
                                <Cross />
                            </button>
                        </div>
                    )}
                    <Tooltip
                        title={open ? '' : unread ? unreadLabel : `${label} · Спросить Лидсманию`}
                        placement="left"
                        enterDelay={450}
                        disableTouchListener
                    >
                        <button
                            ref={launcher}
                            className="lm-companion-launch"
                            onClick={toggle}
                            aria-label={
                                open ? 'Свернуть помощника' : `Открыть помощника — ${unread ? unreadLabel : label}`
                            }
                            aria-haspopup="dialog"
                            aria-expanded={open}
                            aria-controls={open ? 'lm-companion-panel' : undefined}
                        >
                            <CloudOrb
                                state={state}
                                size={mobile ? 64 : 82}
                            />
                            {(unread || unreadEvents.length > 0) && !open && (
                                <span
                                    className="lm-companion-unread"
                                    aria-hidden="true"
                                />
                            )}
                        </button>
                    </Tooltip>
                    <Fade
                        in={open}
                        timeout={{ enter: 160, exit: 120 }}
                        mountOnEnter
                        unmountOnExit
                        onEntered={() => {
                            if (!openRef.current) return;
                            // Opening on a phone should not immediately summon its keyboard.
                            (mobile || command.state ? panel.current : field.current)?.focus({ preventScroll: true });
                            if (log.current)
                                log.current.scrollTop = followBottom.current
                                    ? log.current.scrollHeight
                                    : logScrollTop.current;
                        }}
                    >
                        <section
                            ref={panel}
                            id="lm-companion-panel"
                            role="dialog"
                            aria-modal="false"
                            tabIndex={-1}
                            aria-labelledby="lm-companion-title"
                            className="lm-companion-panel"
                        >
                            <header className="lm-companion-header">
                                <LeadsmaniaCollapsedLogo
                                    width={27}
                                    height={27}
                                />
                                <h2 id="lm-companion-title">Лидсмания</h2>
                                <IconButton
                                    aria-label="Закрыть помощника"
                                    onClick={() => close(true)}
                                >
                                    <Cross />
                                </IconButton>
                            </header>
                            {((!available && summary.state !== 'loading') || reviewUnavailable) && (
                                <div
                                    className="lm-companion-connection"
                                    role="status"
                                >
                                    <p>
                                        {!available
                                            ? 'Состояние потоков недоступно.'
                                            : 'Не удалось обновить очередь диалогов.'}
                                    </p>
                                    <Button
                                        disabled={refreshing}
                                        onClick={() => void refresh()}
                                    >
                                        {refreshing ? 'Обновляю…' : 'Обновить состояние'}
                                    </Button>
                                </div>
                            )}
                            {events.length > 0 && (
                                <nav
                                    className="lm-companion-events"
                                    aria-label="События и действия"
                                >
                                    {events.map((event) => (
                                        <button
                                            key={event.id}
                                            onClick={() => openEvent(event)}
                                        >
                                            <span>
                                                <strong>{event.title}</strong>
                                                <span>{event.subject}</span>
                                            </span>
                                            <span aria-hidden="true">↗</span>
                                        </button>
                                    ))}
                                </nav>
                            )}
                            <div
                                className="lm-companion-log"
                                ref={log}
                                role="log"
                                aria-label="Диалог с помощником"
                                aria-live="polite"
                                aria-relevant="additions text"
                                onScroll={(e) => {
                                    const node = e.currentTarget;
                                    logScrollTop.current = node.scrollTop;
                                    followBottom.current = node.scrollHeight - node.scrollTop - node.clientHeight < 48;
                                }}
                            >
                                {messages.length === 0 && (
                                    <div className="lm-companion-welcome">
                                        <h3>Я рядом. Чем помочь?</h3>
                                        <p>
                                            {currentStream
                                                ? `Помогу с потоком «${getStreamDisplayName(currentStream)}».`
                                                : 'Подскажу по поиску лидов и настройкам.'}
                                        </p>
                                        <div className="lm-companion-suggestions">
                                            {[
                                                currentStream ? 'Что с этим потоком?' : 'Что с моими потоками?',
                                                'Почему нет лидов?',
                                                currentStream
                                                    ? currentStream.is_active
                                                        ? 'Поставь этот поток на паузу'
                                                        : 'Возобнови этот поток'
                                                    : 'Как настроить поиск?',
                                            ].map((q) => (
                                                <button
                                                    key={q}
                                                    onClick={() => void send(q)}
                                                >
                                                    {q}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {messages.map((m) => (
                                    <article
                                        key={m.id}
                                        className={`lm-companion-message lm-companion-message--${m.role}`}
                                    >
                                        <p>{m.text}</p>
                                        {m.actions && m.actions.length > 0 && (
                                            <div className="lm-companion-suggestions">
                                                {m.actions.map((a) => (
                                                    <button
                                                        key={a.path}
                                                        onClick={() => go(a.path)}
                                                    >
                                                        {a.label} ↗
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </article>
                                ))}
                                <StreamCommandCard
                                    command={command}
                                    go={go}
                                />
                                {busy && (
                                    <div
                                        className="lm-companion-thinking"
                                        role="status"
                                    >
                                        <i />
                                        <i />
                                        <i />
                                        <span>{busyLabel}…</span>
                                    </div>
                                )}
                                {error && (
                                    <div
                                        className="lm-companion-error"
                                        role="alert"
                                    >
                                        <p>{error}</p>
                                        {retry && <button onClick={() => void send(retry, true)}>Повторить</button>}
                                    </div>
                                )}
                            </div>
                            <form
                                className="lm-companion-composer"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    void send(input);
                                }}
                            >
                                <textarea
                                    ref={field}
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    disabled={!!command.state}
                                    rows={2}
                                    maxLength={600}
                                    placeholder="Спросите о Лидсмании…"
                                    aria-label="Сообщение помощнику"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                                            e.preventDefault();
                                            void send(input);
                                        }
                                    }}
                                />
                                {busy ? (
                                    <button
                                        type="button"
                                        aria-label="Остановить ответ"
                                        onClick={stop}
                                        className="lm-companion-send"
                                    >
                                        <span className="lm-companion-stop" />
                                    </button>
                                ) : (
                                    <button
                                        type="submit"
                                        className="lm-companion-send"
                                        aria-label="Отправить сообщение"
                                        disabled={!input.trim() || !!command.state}
                                    >
                                        <ArrowLeft />
                                    </button>
                                )}
                            </form>
                            <footer className="lm-companion-footer">
                                {messages.length > 0 && (
                                    <button
                                        disabled={!!command.state}
                                        onClick={() => {
                                            stop();
                                            command.cancel();
                                            setMessages([]);
                                            setError('');
                                            setRetry('');
                                            setInput('');
                                            followBottom.current = true;
                                            field.current?.focus({ preventScroll: true });
                                        }}
                                    >
                                        Новый диалог
                                    </button>
                                )}
                                <a
                                    href={SUPPORT_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Поддержка ↗
                                </a>
                            </footer>
                        </section>
                    </Fade>
                </div>
            </ClickAwayListener>
        </Portal>
    );
}

export default function CabinetCompanion() {
    const authorized = useSessionStore((s) => s.isAuthorized && s.hasSessionSnapshot);
    const { id, confirmed } = useUserStore((s) => ({ id: s.id, confirmed: s.isEmailConfirmed }));
    if (!authorized || !id || !confirmed) return null;
    return (
        <Companion
            key={id}
            userId={id}
        />
    );
}
